/* ============================================================
   NattyPLUS Cart Drawer — REDESIGN runtime (np-cart-drawer.js)  v1.0
   ============================================================
   Self-contained vanilla-JS cart drawer for sections/np-cart-drawer.liquid. Does
   NOT use the theme's compiled bundle.js cart classes. Defines its own custom
   elements and its own open/close + cart-mutation logic.

   NPCartSync COMPATIBILITY (why the method names look "theme-ish")
   ---------------------------------------------------------------
   snippets/np-cart-sync.liquid is the store's single source of truth for cart
   sync (key-based line updates, Appstle free-gift reconciliation, header badge,
   gift-vanish gate, etc.). It drives the drawer through a fixed method contract
   that the old <cart-drawer> exposed. To stay drop-in compatible when this drawer
   is swapped in as id="CartDrawer", <np-cart-drawer> implements the SAME contract:

     isOpen (getter)     purchaseHandler(doc, state)   getTemplateContent(doc)
     open() close()      mount()                       setLoadingStatus(bool)
     setPreEmpty(bool)   updateEmptyStatus(count?)     setCartState()
     emit(name, detail)  updateItem(index, qty)        removeItem()
     showError(msg)      isDrawer=true                 lastDrawerBody / element

   And <np-cart-item> exposes: setLoading(bool), setDisabled(bool), removeItem().

   When np-cart-sync is present it OVERRIDES this element's updateItem with its own
   key-based version (see np-cart-sync wireCart). Our built-in updateItem below is
   the FALLBACK used only when np-cart-sync isn't wired to this element (e.g. while
   it still targets the old #CartDrawer during development).

   DRAWER_ID
   --------
   Must match the element id + template id in np-cart-drawer.liquid. ACTIVATED:
   this is "CartDrawer", so the header <sidebar-button data-sidebar-id="CartDrawer">
   and np-cart-sync target this drawer. (To revert to a parallel dev copy, set the
   liquid id + this constant back to "NPCartDrawer".)
   ============================================================ */

(function () {
  'use strict';
  if (window.__npCartDrawerV) return;
  window.__npCartDrawerV = 1;

  var DRAWER_ID = 'CartDrawer';            // ACTIVATED: matches id in np-cart-drawer.liquid + header sidebar-button + np-cart-sync
  var DRAWER_TAG = 'np-cart-drawer';
  var ITEM_TAG = 'np-cart-item';
  var QTY_INPUT_SEL = '[data-quantity-input]';
  var SCROLL_LOCK_CLASS = 'np-cart-scroll-lock';
  var FALLBACK_DEBOUNCE = 450;
  // Free-gift promo: adding a 90-day supply attaches this free product (Ultimate
  // Travel Support) as a nested child — mirrors np-subscription-widget.liquid
  // (NP_GIFT_VARIANT_ID). Keep in sync with the widget; set to null to disable.
  var NP_GIFT_VARIANT_ID = 59448076599582;

  /* ---------- small helpers ---------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, wait);
    };
  }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  // Apply a chosen recommendation-card option: mark it selected, update the
  // dropdown trigger label, and reflect its price on the card. Pure DOM writes,
  // shared by init + user selection.
  function applyRecSelection(card, opt) {
    $all('[data-np-rec-option]', card).forEach(function (o) {
      o.classList.toggle('is-selected', o === opt);
      if (o === opt) o.setAttribute('aria-selected', 'true'); else o.removeAttribute('aria-selected');
    });
    // Trigger label — separate spans: name, price-now, period, strikethrough was.
    var name = opt.getAttribute('data-name') || '';
    var priceNow = opt.getAttribute('data-price-now') || '';
    var per = opt.getAttribute('data-per') || '';
    var priceWas = opt.getAttribute('data-price-was') || '';
    var nameEl = card.querySelector('[data-np-rec-select-name]');
    var trigNow = card.querySelector('[data-np-rec-select-now]');
    var trigPer = card.querySelector('[data-np-rec-select-per]');
    var trigWas = card.querySelector('[data-np-rec-select-was]');
    if (nameEl) nameEl.textContent = name;
    if (trigNow) trigNow.textContent = priceNow;
    if (trigPer) trigPer.textContent = per;
    if (trigWas) {
      if (priceWas) { trigWas.textContent = priceWas; trigWas.removeAttribute('hidden'); }
      else { trigWas.textContent = ''; trigWas.setAttribute('hidden', ''); }
    }
    // Card price (now + was)
    var now = card.querySelector('[data-np-rec-price-now]');
    var was = card.querySelector('[data-np-rec-price-was]');
    if (now) now.textContent = opt.getAttribute('data-price-now') || now.textContent;
    if (was) {
      var w = opt.getAttribute('data-price-was');
      if (w) { was.textContent = w; was.removeAttribute('hidden'); }
      else { was.textContent = ''; was.setAttribute('hidden', ''); }
    }
  }

  // Repair a just-added subscription line whose selling_plan was silently
  // STRIPPED en route to /cart/add.js.
  //
  // WHY THIS EXISTS: on a product's OWN PDP, an installed subscription app
  // (Appstle) monkey-patches fetch / runs cart middleware that reconciles adds
  // of "its" variant. When the rec card adds the SAME variant as the current PDP
  // product WITH a selling_plan, the app strips the plan — the /cart/add.js call
  // returns 200 OK but the line comes back as ONE-TIME. (The 90-day option adds a
  // DIFFERENT product's variant, which the app doesn't own, so it was unaffected;
  // and on non-PDP pages the app's PDP interceptor isn't active, so 30-day worked
  // there too — exactly the reported symptom.)
  //
  // FIX: mirror snippets/np-subscription-widget.liquid — after the add, if the
  // line for `variantId` did NOT come back with `expectedPlanId`, locate that
  // line in /cart.js and rewrite it via /cart/change.js to attach the plan.
  // Returns a promise that resolves once the repair is attempted (best-effort;
  // never rejects, so it can't break the add flow).
  function npRepairStrippedPlan(addResp, variantId, expectedPlanId) {
    if (!expectedPlanId || !addResp || !Array.isArray(addResp.items)) return Promise.resolve();
    var addedItem = null;
    for (var i = 0; i < addResp.items.length; i++) {
      if (addResp.items[i].variant_id === variantId) { addedItem = addResp.items[i]; break; }
    }
    if (!addedItem) addedItem = addResp.items[addResp.items.length - 1];
    var appliedPlanId = addedItem && addedItem.selling_plan_allocation && addedItem.selling_plan_allocation.selling_plan
      ? addedItem.selling_plan_allocation.selling_plan.id
      : null;
    if (appliedPlanId === expectedPlanId) return Promise.resolve();   // plan survived — nothing to do

    console.warn('[np-cart-rec-card] /cart/add.js stripped selling_plan (sent ' + expectedPlanId +
      ', got ' + appliedPlanId + '). Repairing via /cart/change.js.');

    return fetch('/cart.js', { credentials: 'include' }).then(function (r) {
      if (!r.ok) throw new Error('cart.js ' + r.status);
      return r.json();
    }).then(function (cart) {
      var items = cart.items || [];
      var lineNumber = null, matchedLine = null;
      // Search from the END so we grab the line we just added, not an older
      // one-time line of the same variant.
      for (var j = items.length - 1; j >= 0; j--) {
        var li = items[j];
        if (li.variant_id !== variantId) continue;
        var liPlanId = li.selling_plan_allocation && li.selling_plan_allocation.selling_plan
          ? li.selling_plan_allocation.selling_plan.id : null;
        if (liPlanId !== expectedPlanId) { lineNumber = j + 1; matchedLine = li; break; }
      }
      if (!lineNumber) return;
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          line: lineNumber,
          selling_plan: expectedPlanId,
          quantity: (matchedLine && matchedLine.quantity) || 1
        })
      }).then(function (cr) {
        if (!cr.ok) throw new Error('change.js ' + cr.status);
        return cr.json();
      }).then(function (changeResp) {
        var fixed = changeResp.items && changeResp.items[lineNumber - 1];
        var fixedPlanId = fixed && fixed.selling_plan_allocation && fixed.selling_plan_allocation.selling_plan
          ? fixed.selling_plan_allocation.selling_plan.id : null;
        if (fixedPlanId !== expectedPlanId) {
          console.warn('[np-cart-rec-card] /cart/change.js did NOT re-apply selling_plan (line still ' +
            fixedPlanId + '). App is intercepting BOTH endpoints.');
        }
      });
    }).catch(function (e) {
      console.warn('[np-cart-rec-card] stripped-plan repair failed:', e && e.message ? e.message : e);
    });
  }

  // Ensure the scroll-lock style exists once (kept in JS so the asset is self-contained).
  function ensureScrollLockStyle() {
    if (document.getElementById('np-cart-drawer-runtime-style')) return;
    var s = document.createElement('style');
    s.id = 'np-cart-drawer-runtime-style';
    s.textContent =
      'html.' + SCROLL_LOCK_CLASS + ', body.' + SCROLL_LOCK_CLASS +
      '{overflow:hidden !important;touch-action:none;}';
    document.head.appendChild(s);
  }

  /* ============================================================
     <np-quantity-btn>  (plus / minus; minus-at-1 removes)
     ============================================================ */
  function defineQuantityBtn() {
    if (customElements.get('np-quantity-btn')) return;
    customElements.define('np-quantity-btn', class extends HTMLElement {
      connectedCallback() {
        if (this.__wired) return;
        this.__wired = true;
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.onClick(e); }
        }.bind(this));
      }
      get component() { return this.closest('np-quantity-component'); }
      onClick(e) {
        e.preventDefault();
        var comp = this.component;
        if (!comp) return;
        var action = this.dataset.action;
        var input = comp.querySelector(QTY_INPUT_SEL);
        if (!input) return;
        var val = parseInt(input.value, 10) || 0;
        // minus at 1 -> remove the line (drives updateItem(index, 0))
        if (action === 'minus' && val <= 1) {
          var item = this.closest(ITEM_TAG);
          var cart = this.closest(DRAWER_TAG);
          if (item && cart && typeof cart.updateItem === 'function') {
            cart.updateItem(parseInt(item.dataset.index, 10), 0);
          }
          return;
        }
        if (action === 'plus') input.stepUp(); else input.stepDown();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /* ============================================================
     <np-quantity-component>
     ============================================================ */
  function defineQuantityComponent() {
    if (customElements.get('np-quantity-component')) return;
    customElements.define('np-quantity-component', class extends HTMLElement {
      setDisable(isDisabled) {
        $all('np-quantity-btn', this).forEach(function (b) { b.toggleAttribute('disabled', !!isDisabled); });
        var input = this.querySelector(QTY_INPUT_SEL);
        // Never re-enable an app-owned free-gift input.
        if (input) input.disabled = !!isDisabled || !!input.closest('[data-np-free-gift]');
      }
    });
  }

  /* ============================================================
     <np-cart-remove-button>  (trash icon)
     ============================================================ */
  function defineRemoveButton() {
    if (customElements.get('np-cart-remove-button')) return;
    customElements.define('np-cart-remove-button', class extends HTMLElement {
      connectedCallback() {
        if (this.__wired) return;
        this.__wired = true;
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.onClick(e); }
        }.bind(this));
      }
      onClick(e) {
        e.preventDefault();
        var cart = this.closest(DRAWER_TAG);
        var index = parseInt(this.dataset.index, 10);
        if (cart && typeof cart.updateItem === 'function' && !isNaN(index)) {
          cart.updateItem(index, 0);
        }
      }
    });
  }

  /* ============================================================
     <np-cart-item>
     ============================================================ */
  function defineCartItem() {
    if (customElements.get(ITEM_TAG)) return;
    customElements.define(ITEM_TAG, class extends HTMLElement {
      // is-loading attribute -> per-line spinner (CSS).
      setLoading(isLoading) { this.toggleAttribute('is-loading', !!isLoading); }

      // Disable this line's quantity stepper.
      setDisabled(isDisabled) {
        var q = this.querySelector('[data-cart-item-quantity]');
        if (q && typeof q.setDisable === 'function') q.setDisable(!!isDisabled);
      }

      // Collapse the row with a height transition (mirrors the theme's removeItem).
      removeItem() {
        var self = this;
        return new Promise(function (resolve) {
          var h = self.offsetHeight;
          self.style.maxHeight = h + 'px';
          // force reflow so the transition runs from the measured height
          void self.offsetHeight;
          requestAnimationFrame(function () {
            self.style.maxHeight = '0px';
            self.style.opacity = '0';
          });
          var done = false;
          function finish() { if (done) return; done = true; resolve(); }
          self.addEventListener('transitionend', finish, { once: true });
          setTimeout(finish, 350); // safety if transitionend never fires
        });
      }
    });
  }

  /* ============================================================
     <np-cart-drawer>
     ============================================================ */
  function defineCartDrawer() {
    if (customElements.get(DRAWER_TAG)) return;

    customElements.define(DRAWER_TAG, class extends HTMLElement {
      connectedCallback() {
        if (this.__wired) return;
        this.__wired = true;
        this.isDrawer = true;
        this._open = false;
        this.overlay = null;

        ensureScrollLockStyle();
        this.ensureOverlay();
        this.bindShellEvents();       // Escape key + trigger delegation (cheap, needed now)
        // The body (line items, subtotal, recs markup) isn't visible until the
        // drawer opens, so mounting it is deferred off the critical page-load path
        // to idle time. open()/purchaseHandler ensure it's mounted before any use,
        // so behavior is identical — the work just no longer blocks initial load.
        this.__mounted = false;
        var self = this;
        var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 200); };
        idle(function () { if (!self.__mounted) self.mount(); });
      }

      // Guarantee the body is mounted before any code that reads/shows it.
      ensureMounted() { if (!this.__mounted) this.mount(); }

      /* ---------- template / mount ---------- */

      // Extract the drawer body node from a section Document (np-cart-sync passes one).
      getTemplateContent(doc) {
        var tmpl = (doc || document).querySelector('[data-sidebar-template="' + this.id + '"]')
          || (doc || this).querySelector('template[data-sidebar-template]');
        if (tmpl && tmpl.content) {
          var frag = tmpl.content.cloneNode(true);
          return frag.firstElementChild || frag;
        }
        // Already-inflated body (rare path).
        var body = (doc || this).querySelector('[data-sidebar-body]');
        return body ? body.cloneNode(true) : null;
      }

      // Render the drawer body into the live element. `this.element` is the CANONICAL
      // (detached) body node kept as the source of truth — np-cart-sync may swap it.
      // We always mount a CLONE so the canonical copy survives repeated re-renders.
      mount(node) {
        // Resolve the canonical body: an explicit node, the cached one, or the
        // template's content on first mount.
        var canonical = node || this.element || this.getTemplateContent(this);
        if (!canonical) return;
        this.element = canonical;
        // Reset the once-per-body wiring guards so the fresh DOM gets re-bound.
        this.__changeWired = false;
        this.replaceChildren(this.element.cloneNode(true));
        this.__mounted = true;
        this.wireItems();
        this.observeItems();
        this.bindBodyEvents();
        this.updateShadows();
        this.loadRelatedProducts();
        this.placeAside();
      }

      /* ---------- responsive aside placement ----------
         Desktop (>768px): the recommendations column (.np-cart-drawer__body-aside-content)
         lives as a sibling of main-content (the side column).
         Mobile (<=768px): move it INTO main-content, right before the footer, so the
         recs stack above the subtotal/checkout instead of being hidden. Re-runs on
         mount and on resize; idempotent (no-op if already in the right place). */
      placeAside() {
        var aside = this.querySelector('.np-cart-drawer__body-aside-content');
        if (!aside) return;
        var body = this.querySelector('.np-cart-drawer__body');
        var main = this.querySelector('.np-cart-drawer__body-main-content');
        var footer = main && main.querySelector('.np-cart-drawer__footer');
        if (!body || !main || !footer) return;

        var isMobile = window.matchMedia('(max-width: 768px)').matches;
        if (isMobile) {
          // Should sit inside main-content, immediately before the footer.
          if (aside.parentNode !== main || aside.nextElementSibling !== footer) {
            main.insertBefore(aside, footer);
          }
          aside.classList.add('np-cart-drawer__body-aside-content--in-body');
        } else {
          // Should sit as the last child of body (the side column).
          if (aside.parentNode !== body) {
            body.appendChild(aside);
          }
          aside.classList.remove('np-cart-drawer__body-aside-content--in-body');
        }
        // Update now, and again after the layout reflow so the button state reflects
        // the row-slider dimensions (not the pre-relocation column layout).
        this.updateRecNav();
        var self = this;
        requestAnimationFrame(function () { self.updateRecNav(); });
      }

      /* ---------- open / close ---------- */
      get isOpen() { return this._open; }

      ensureOverlay() {
        if (this.overlay && document.body.contains(this.overlay)) return;
        var ov = document.createElement('div');
        ov.className = 'np-cart-drawer-overlay';
        ov.setAttribute('data-np-cart-overlay', '');
        document.body.appendChild(ov);
        this.overlay = ov;
        ov.addEventListener('click', this.close.bind(this));
      }

      open() {
        this.ensureMounted();   // in case the idle mount hasn't run yet
        this.ensureOverlay();
        this._open = true;
        this.setAttribute('is-opened', '');
        this.overlay.classList.add('is-visible');
        document.documentElement.classList.add(SCROLL_LOCK_CLASS);
        document.body.classList.add(SCROLL_LOCK_CLASS);
        // reflect state on the header trigger(s)
        $all('[data-sidebar-button][data-sidebar-id="' + this.id + '"]').forEach(function (b) {
          b.classList.add('is-opened');
        });
        this.updateShadows();
        this.emit('np:drawer-open', {});
        return Promise.resolve();
      }

      close() {
        this._open = false;
        this.removeAttribute('is-opened');
        if (this.overlay) this.overlay.classList.remove('is-visible');
        document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
        document.body.classList.remove(SCROLL_LOCK_CLASS);
        $all('[data-sidebar-button][data-sidebar-id="' + this.id + '"]').forEach(function (b) {
          b.classList.remove('is-opened');
        });
        this.emit('np:drawer-close', {});
        return Promise.resolve();
      }

      toggle() { return this._open ? this.close() : this.open(); }

      bindShellEvents() {
        var self = this;
        // Close on Escape.
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && self._open) self.close();
        });
        // Re-place the aside column when crossing the mobile breakpoint.
        if (!this.__asideMql && window.matchMedia) {
          this.__asideMql = window.matchMedia('(max-width: 768px)');
          var onChange = function () { self.placeAside(); };
          if (this.__asideMql.addEventListener) this.__asideMql.addEventListener('change', onChange);
          else if (this.__asideMql.addListener) this.__asideMql.addListener(onChange); // older Safari
        }
        // Recommendations slider prev/next — delegated so it survives bundle.js
        // re-rendering the recs content (fresh buttons, same handler).
        if (!this.__recNavWired) {
          this.__recNavWired = true;
          this.addEventListener('click', function (e) {
            var btn = e.target.closest && e.target.closest('[data-np-rec-prev],[data-np-rec-next]');
            if (!btn) return;
            e.preventDefault();
            self.scrollRecSlider(btn.hasAttribute('data-np-rec-next') ? 1 : -1, btn);
          });
        }
        // Recommendation-card buy dropdown + add-to-cart — delegated (survives
        // bundle.js re-render). See np-cart-rec-card.liquid for the data-* contract.
        if (!this.__recCardWired) {
          this.__recCardWired = true;
          this.addEventListener('click', function (e) {
            var t = e.target;
            // Dropdown trigger → toggle
            var trigger = t.closest && t.closest('[data-np-rec-select-trigger]');
            if (trigger) { e.preventDefault(); self.toggleRecSelect(trigger.closest('[data-np-rec-select]')); return; }
            // Option chosen
            var opt = t.closest && t.closest('[data-np-rec-option]');
            if (opt && opt.closest('[data-np-rec-select-list]')) {
              e.preventDefault();
              self.selectRecOption(opt.closest('np-cart-rec-card'), opt);
              return;
            }
            // Quantity stepper
            var minus = t.closest && t.closest('[data-np-rec-qty-minus]');
            if (minus) { e.preventDefault(); self.stepRecQty(minus.closest('np-cart-rec-card'), -1); return; }
            var plus = t.closest && t.closest('[data-np-rec-qty-plus]');
            if (plus) { e.preventDefault(); self.stepRecQty(plus.closest('np-cart-rec-card'), 1); return; }
            // Add to cart
            var add = t.closest && t.closest('[data-np-rec-add]');
            if (add) { e.preventDefault(); self.recCardAdd(add.closest('np-cart-rec-card'), add); return; }
            // Click elsewhere closes any open dropdown
            self.closeAllRecSelects();
          });
          this.addEventListener('change', function (e) {
            // Clamp a directly-typed quantity to a positive integer.
            var input = e.target.closest && e.target.closest('[data-np-rec-qty-input]');
            if (input) {
              var n = parseInt(input.value, 10);
              input.value = (isNaN(n) || n < 1) ? '1' : String(n);
              return;
            }
            // Native mobile <select> chosen → apply the matching <li> option.
            var native = e.target.closest && e.target.closest('[data-np-rec-native]');
            if (native) {
              var card = native.closest('np-cart-rec-card');
              var opt = card && card.querySelector('[data-np-rec-option][data-opt-index="' + native.value + '"]');
              if (card && opt) applyRecSelection(card, opt);
            }
          });
        }
        this.bindBodyEvents();
      }

      /* ---------- recommendation-card buy dropdown ---------- */
      // Set each card's initial selected option (its [data-default]) + label/price.
      initRecCards() {
        $all('np-cart-rec-card', this).forEach(function (card) {
          if (card.__npRecInit) return;
          card.__npRecInit = true;
          var def = card.querySelector('[data-np-rec-option][data-default]') || card.querySelector('[data-np-rec-option]');
          if (def) applyRecSelection(card, def);
        });
      }

      // True when this select is on the in-body mobile slider — there the NATIVE
      // <select> overlay handles selection (see np-cart-rec-card.liquid + CSS), so
      // the custom popover must NOT open.
      recIsMobileSlider(sel) {
        return !!(sel && sel.closest('.np-cart-drawer__body-aside-content--in-body'));
      }

      toggleRecSelect(sel) {
        if (!sel) return;
        // Mobile uses the native <select>; don't open the custom menu there.
        if (this.recIsMobileSlider(sel)) return;
        var open = sel.classList.contains('is-open');
        this.closeAllRecSelects();
        if (!open) {
          sel.classList.add('is-open');
          var trg = sel.querySelector('[data-np-rec-select-trigger]');
          if (trg) trg.setAttribute('aria-expanded', 'true');
        }
      }

      closeAllRecSelects() {
        $all('[data-np-rec-select].is-open', this).forEach(function (sel) {
          sel.classList.remove('is-open');
          var trg = sel.querySelector('[data-np-rec-select-trigger]');
          if (trg) trg.setAttribute('aria-expanded', 'false');
        });
      }

      selectRecOption(card, opt) {
        if (!card || !opt) return;
        applyRecSelection(card, opt);
        this.closeAllRecSelects();
      }

      // Read the shopper-chosen quantity from a rec card's stepper (clamped ≥1).
      recCardQty(card) {
        var input = card && card.querySelector('[data-np-rec-qty-input]');
        var n = input ? parseInt(input.value, 10) : 1;
        if (isNaN(n) || n < 1) n = 1;
        return n;
      }

      // Adjust a rec card's quantity stepper by `delta`, clamped to ≥1.
      stepRecQty(card, delta) {
        var input = card && card.querySelector('[data-np-rec-qty-input]');
        if (!input) return;
        var n = parseInt(input.value, 10);
        if (isNaN(n)) n = 1;
        n += delta;
        if (n < 1) n = 1;
        input.value = String(n);
      }

      // Build the /cart/add.js payload from the card's SELECTED option and add.
      recCardAdd(card, btn) {
        if (!card || card.__npAdding) return;
        var opt = card.querySelector('[data-np-rec-option].is-selected')
          || card.querySelector('[data-np-rec-option][data-default]')
          || card.querySelector('[data-np-rec-option]');
        if (!opt) return;

        card.__npAdding = true;
        var textEl = btn && btn.querySelector('[data-np-rec-add-text]');
        var original = textEl ? textEl.textContent : '';
        if (btn) btn.disabled = true;
        if (btn) btn.classList.add('loading');
        if (textEl) textEl.textContent = 'Adding…';

        var variantId = parseInt(opt.getAttribute('data-variant-id'), 10);
        var bundle = parseInt(opt.getAttribute('data-bundle'), 10) || 1;
        // Quantity the shopper picked in the stepper × the option's bundle multiplier
        // (bundle is 1 for every current option — the 90-day is its OWN product/variant,
        // so it must add 1 unit, not 3). qty defaults to 1 if the stepper is absent.
        var qty = this.recCardQty(card) * bundle;
        var isBundle = opt.getAttribute('data-is-bundle') === 'true';
        var item = { id: variantId, quantity: qty };
        // The plan we EXPECT on the resulting line. Used to detect (and repair)
        // the case where an app strips selling_plan from /cart/add.js — see
        // npRepairStrippedPlan. null for one-time options.
        var expectedPlanId = null;
        if (opt.getAttribute('data-purchase') === 'subscription' && opt.getAttribute('data-plan-id')) {
          expectedPlanId = parseInt(opt.getAttribute('data-plan-id'), 10);
          item.selling_plan = expectedPlanId;
        }
        if (isBundle && opt.getAttribute('data-parent-handle')) {
          item.properties = { _np_parent_handle: opt.getAttribute('data-parent-handle') };
        }

        // Same switch as the PDP widget: theme setting np_choice_bottle_gift,
        // stamped on this element by np-cart-drawer.liquid. On = Halloween
        // choice-bottle event, no automatic Travel Support. Off = attach it.
        var choiceGift = this.getAttribute('data-np-choice-bottle-gift') === 'true';
        var addGift = choiceGift ? false : isBundle && NP_GIFT_VARIANT_ID;
        var items = [item];
        if (addGift) {
          var pairToken = 'np-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
          item.properties = Object.assign({}, item.properties, {
            _np_gift_parent: '1',
            _np_pair: pairToken
          });
          items.push({
            id: NP_GIFT_VARIANT_ID,
            quantity: qty,                 // mirror the parent so buy-N → N free
            parent_id: variantId,          // Shopify nests the gift under the parent
            properties: { _np_gift_for: String(variantId), _np_pair: pairToken }
          });
        }

        function restore(label) {
          if (btn) { btn.disabled = false; btn.classList.remove('loading'); }
          if (textEl) textEl.textContent = label || original || 'Add to cart';
          card.__npAdding = false;
        }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ items: items })
        }).then(function (res) {
          if (!res.ok) return res.json().catch(function () { return {}; }).then(function (err) {
            throw new Error(err.description || err.message || 'Add to cart failed');
          });
          return res.json();
        }).then(function (addResp) {
          // If a subscription plan was requested but the returned line came back
          // one-time (an app stripped it), re-apply it via /cart/change.js BEFORE
          // we refresh the drawer, so the drawer renders the corrected line.
          return npRepairStrippedPlan(addResp, variantId, expectedPlanId);
        }).then(function () {
          // Refresh + open the drawer via the store's cart-sync single source of truth.
          if (window.NPCartSync && typeof window.NPCartSync.refresh === 'function') {
            return window.NPCartSync.refresh({ open: true });
          }
        }).then(function () {
          restore('Added ✓');
          setTimeout(function () { if (textEl) textEl.textContent = original || 'Add to cart'; }, 1500);
        }).catch(function (e) {
          console.warn('[np-cart-rec-card] add failed:', e && e.message ? e.message : e);
          restore('Try again');
        });
      }

      // Scroll the recommendations slider that contains `nearBtn` by one slide.
      // Index-based (not relative scrollBy): compute the CURRENT card index from
      // scrollLeft, step by ±1, clamp, and scrollTo the exact card offset. This is
      // robust against rapid clicks — every target is a clean snap position, so
      // fast prev/next can't leave the carousel stranded on a half-cut card.
      scrollRecSlider(dir, nearBtn) {
        var host = nearBtn ? nearBtn.closest('[data-cart-drawer-related-products]') : this;
        var track = host && host.querySelector('[data-np-rec-track]');
        if (!track) return;
        var first = track.firstElementChild;
        var step = first ? first.getBoundingClientRect().width : track.clientWidth;
        if (step <= 0) return;
        var count = track.children.length;
        var maxIndex = Math.max(0, count - 1);
        var current = Math.round(track.scrollLeft / step);
        var target = Math.min(maxIndex, Math.max(0, current + dir));
        track.scrollTo({ left: target * step, behavior: 'smooth' });
      }

      // Enable/disable the prev/next buttons. Only the in-body mobile slider pages;
      // the desktop column is a vertical stack (no paging). Uses the CARD COUNT +
      // current index (not scrollWidth, which is 0 before the row layout reflows or
      // before bundle.js injects the cards), so the buttons enable as soon as there
      // are ≥2 cards on the slider.
      updateRecNav() {
        var drawer = this;
        $all('[data-np-rec-nav]', this).forEach(function (nav) {
          var host = nav.closest('[data-cart-drawer-related-products]');
          var track = host && host.querySelector('[data-np-rec-track]');
          var prev = nav.querySelector('[data-np-rec-prev]');
          var next = nav.querySelector('[data-np-rec-next]');
          var isSlider = !!nav.closest('.np-cart-drawer__body-aside-content--in-body');
          var count = track ? track.children.length : 0;
          if (!track || !isSlider || count < 2) {
            if (prev) prev.disabled = true;
            if (next) next.disabled = true;
            return;
          }
          var step = track.firstElementChild ? track.firstElementChild.getBoundingClientRect().width : track.clientWidth;
          var index = step > 0 ? Math.round(track.scrollLeft / step) : 0;
          if (prev) prev.disabled = index <= 0;
          if (next) next.disabled = index >= count - 1;
        });
      }

      // Events on the (re-rendered) body: close button + scroll shadows.
      bindBodyEvents() {
        var self = this;
        var closeBtn = this.querySelector('[data-np-cart-close]');
        if (closeBtn && !closeBtn.__wired) {
          closeBtn.__wired = true;
          closeBtn.addEventListener('click', function (e) { e.preventDefault(); self.close(); });
        }
        var viewport = this.querySelector('[data-cart-drawer-viewport]');
        if (viewport && !viewport.__npScroll) {
          viewport.__npScroll = true;
          viewport.addEventListener('scroll', function () {
            self.updateShadows();
            // A floating (fixed) dropdown doesn't follow scroll — close it so it
            // never ends up detached from its trigger.
            if (self.querySelector('[data-np-rec-select].is-open')) self.closeAllRecSelects();
          }, { passive: true });
        }
        // Update the slider arrows' disabled state as the track scrolls, and close
        // any open (floating) dropdown so it can't detach from its card.
        $all('[data-np-rec-track]', this).forEach(function (track) {
          if (track.__npRecScroll) return;
          track.__npRecScroll = true;
          track.addEventListener('scroll', function () {
            self.updateRecNav();
            if (self.querySelector('[data-np-rec-select].is-open')) self.closeAllRecSelects();
          }, { passive: true });
        });
        this.updateRecNav();
        this.initRecCards();   // set each rec card's default option label/price
      }

      updateShadows() {
        var vp = this.querySelector('[data-cart-drawer-viewport]');
        if (!vp) return;
        var atTop = vp.scrollTop <= 1;
        var atBottom = vp.scrollTop + vp.clientHeight >= vp.scrollHeight - 1;
        this.classList.toggle('np-header-shadow-visible', !atTop);
        this.classList.toggle('np-footer-shadow-visible', !atBottom);
      }

      /* ---------- state helpers (np-cart-sync contract) ---------- */
      setLoadingStatus(isLoading) { this.classList.toggle('is-cart-loading', !!isLoading); }
      setPreEmpty(isEmpty) { this.classList.toggle('is-pre-empty', !!isEmpty); }

      updateEmptyStatus(isEmpty) {
        var empty = (typeof isEmpty === 'boolean')
          ? isEmpty
          : this.querySelectorAll(ITEM_TAG).length === 0;
        this.toggleAttribute('is-empty', empty);
        this.toggleAttribute('empty-cart', empty);
        if (empty) this.emit('cartIsEmpty', {});
      }

      setCartState() { this.updateEmptyStatus(); }

      // Minimal event bus so np-cart-sync's emit('update-nodes', ...) reaches
      // listeners AND drives our own re-render.
      emit(name, detail) {
        if (name === 'update-nodes' && detail && detail.node) {
          this.applyUpdateNodes(detail.node);
        }
        try { this.dispatchEvent(new CustomEvent(name, { detail: detail || {}, bubbles: true })); } catch (e) {}
      }
      on(name, handler) { this.addEventListener(name, handler); }
      off(name, handler) { this.removeEventListener(name, handler); }

      // Patch the live body from a freshly-rendered node (a body element or a
      // section Document). Used by np-cart-sync's in-place update path.
      applyUpdateNodes(node) {
        var freshBody = null;
        if (node.nodeType === 9 /* Document */ || node.querySelector) {
          freshBody = node.querySelector ? (node.querySelector('[data-sidebar-body]') || null) : null;
        }
        if (!freshBody && node.matches && node.matches('[data-sidebar-body]')) freshBody = node;
        if (!freshBody && node.getAttribute && node.getAttribute('data-sidebar-body') != null) freshBody = node;
        if (freshBody) {
          this.element = freshBody.cloneNode(true);
          this.replaceChildren(this.element.cloneNode(true));
          this.__mounted = true;   // body is now live; skip a redundant idle mount
          this.wireItems();
          this.observeItems();
          this.bindBodyEvents();
          this.updateShadows();
          this.loadRelatedProducts();
          // The fresh server-rendered body has the aside in its DESKTOP position
          // (sibling of main-content). Re-run placeAside so on mobile it moves back
          // in-body — without this, removing/updating an item reverts the layout.
          this.placeAside();
        }
      }

      // np-cart-sync opens/refreshes via purchaseHandler(sectionDoc, state).
      purchaseHandler(doc, parsedState) {
        this.setPreEmpty(false);
        var body = this.getTemplateContent(doc);
        if (body) this.mount(body);
        // Mirror empty-state attrs from the fresh section.
        var fresh = (doc && doc.querySelector) ? (doc.querySelector(DRAWER_TAG) || doc.querySelector('#' + this.id)) : null;
        if (fresh) {
          this.toggleAttribute('is-empty', fresh.hasAttribute('is-empty'));
          this.toggleAttribute('empty-cart', fresh.hasAttribute('empty-cart'));
          this.toggleAttribute('has-aside-products', fresh.hasAttribute('has-aside-products'));
        } else {
          this.updateEmptyStatus();
        }
        this.updateHeaderCountFromDoc(doc);
        return this.open();
      }

      updateHeaderCountFromDoc(doc) {
        if (!doc || !doc.querySelector) return;
        var newCount = doc.querySelector('#NewCartCount');
        if (!newCount) return;
        $all('#CartCount').forEach(function (el) {
          el.replaceChildren.apply(el, Array.prototype.slice.call(newCount.cloneNode(true).childNodes));
        });
      }

      showError(message) {
        var notif = this.querySelector('#' + this.id + 'Notification, notification-component');
        var textEl = notif && notif.querySelector('[data-notification-alert-text]');
        if (textEl) textEl.textContent = message || 'Something went wrong.';
        if (notif) {
          notif.classList.add('is-visible');
          clearTimeout(this.__errTimer);
          this.__errTimer = setTimeout(function () { notif.classList.remove('is-visible'); }, 6000);
        } else if (message) {
          console.warn('[np-cart-drawer]', message);
        }
      }

      /* ---------- item wiring ---------- */
      wireItems() {
        var self = this;
        if (this.__changeWired) return;
        this.__changeWired = true;
        // Fallback quantity handling — only used if np-cart-sync hasn't taken over
        // updateItem on this element (it stops propagation at capture phase when it has).
        var timers = {};
        this.addEventListener('change', function (e) {
          var input = e.target;
          if (!input || !input.matches || !input.matches(QTY_INPUT_SEL)) return;
          if (input.closest('[data-np-free-gift]')) return;         // never edit gift qty
          if (!self.__npFallbackUpdate) return;                     // np-cart-sync owns updateItem
          var index = parseInt(input.dataset.index, 10);
          if (isNaN(index)) return;
          var qty = parseInt(input.value, 10) || 0;
          clearTimeout(timers[index]);
          timers[index] = setTimeout(function () { self.updateItem(index, qty); }, FALLBACK_DEBOUNCE);
        });
      }

      observeItems() {
        var self = this;
        if (this.__giftObserver || !window.MutationObserver) return;
        this.__giftObserver = new MutationObserver(function () {
          self.updateShadows();
          // bundle.js injects the recommendation cards asynchronously (a childList
          // mutation). Re-attach the track scroll listener + refresh arrow state
          // once they land. bindBodyEvents is idempotent (guarded), so this is cheap.
          self.bindBodyEvents();
        });
        this.__giftObserver.observe(this, { childList: true, subtree: true });
      }

      /* ---------- FALLBACK cart mutation (used only without np-cart-sync) ----------
         np-cart-sync overrides updateItem with a key-based version. This built-in
         version is a safety net so the drawer still works standalone. It uses the
         stable line key too, and re-renders from the Section Rendering response. */
      updateItem(itemIndex, quantity) {
        this.__npFallbackUpdate = true; // mark that WE are the updater (see wireItems)
        var self = this;
        var item = this.querySelector('#CartItem-' + itemIndex);
        if (!item) return Promise.resolve();
        var current = parseInt(item.dataset.quantity || '0', 10);
        if (current === quantity) return Promise.resolve();

        this.setLoadingStatus(true);
        item.setLoading && item.setLoading(true);
        item.setDisabled && item.setDisabled(true);
        if (quantity === 0 && item.removeItem) {
          item.removeItem();
          this.setPreEmpty(this.querySelectorAll(ITEM_TAG).length === 1);
        }

        var sectionId = this.dataset.sectionId;
        var body = {
          quantity: quantity,
          sections: sectionId,
          sections_url: window.location.pathname
        };
        if (item.dataset.key) body.id = item.dataset.key;
        else body.line = (parseInt(item.dataset.index, 10) || 0) + 1;

        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body)
        }).then(function (res) {
          return res.text().then(function (txt) {
            var data = {};
            try { data = JSON.parse(txt); } catch (e) {}
            return { ok: res.ok, status: res.status, data: data };
          });
        }).then(function (r) {
          if (!r.ok || r.data.errors || (typeof r.data.status === 'number' && r.data.status >= 400)) {
            throw new Error(r.data.description || r.data.message || ('cart change ' + r.status));
          }
          var html = r.data.sections && r.data.sections[sectionId];
          if (html != null) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            self.applyFromSectionDoc(doc);
          } else {
            self.refreshFromServer();
          }
        }).catch(function (e) {
          self.showError(e && e.message);
          self.refreshFromServer();
        }).then(function () {
          self.setLoadingStatus(false);
        });
      }

      removeItem() { /* no-op: removal is per-line via updateItem(index,0) */ }

      // Re-render the whole body from a parsed section Document (fallback path).
      applyFromSectionDoc(doc) {
        var body = this.getTemplateContent(doc);
        if (body) this.mount(body);
        var fresh = doc.querySelector(DRAWER_TAG) || doc.querySelector('#' + this.id);
        if (fresh) {
          this.toggleAttribute('is-empty', fresh.hasAttribute('is-empty'));
          this.toggleAttribute('empty-cart', fresh.hasAttribute('empty-cart'));
          this.toggleAttribute('has-aside-products', fresh.hasAttribute('has-aside-products'));
        } else {
          this.updateEmptyStatus();
        }
        this.updateHeaderCountFromDoc(doc);
      }

      // Fetch this section fresh (fallback path).
      refreshFromServer() {
        var self = this;
        var sectionId = this.dataset.sectionId;
        if (!sectionId) return Promise.resolve();
        return fetch('/?sections=' + encodeURIComponent(sectionId), {
          credentials: 'include', headers: { Accept: 'application/json' }
        }).then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            var html = data && data[sectionId];
            if (html == null) return;
            self.applyFromSectionDoc(new DOMParser().parseFromString(html, 'text/html'));
          }).catch(function () {});
      }

      /* ---------- related products (auto recommendations) ----------
         The section now renders the THEME's own <cart-drawer-related-products>
         elements (from bundle.js), whose CartDrawerRelatedProducts custom element
         fetches data-url + injects the cards itself on connect. When we re-render
         the body via mount()/applyUpdateNodes(), the fresh <cart-drawer-related-products>
         clones re-connect and bundle.js re-fetches automatically. So we deliberately
         do NOT fetch here — doing so would double-load and fight bundle.js.
         Kept as a hook (called from mount/update) in case a future non-theme host
         needs custom loading. */
      loadRelatedProducts() { /* handled by the theme's cart-drawer-related-products element */ }
    });
  }

  /* ============================================================
     Header trigger: <sidebar-button data-sidebar-id="NPCartDrawer">
     ------------------------------------------------------------
     The theme's own <sidebar-button> element (from bundle.js) will try to open a
     sidebar with matching id by calling sidebar.toggle(). Our element implements
     toggle(), so that path works. But to be robust even if that element resolves
     to the OLD drawer (same-id conflict during dev) or isn't a real component
     here, we also intercept clicks in capture phase and drive our drawer directly.
     ============================================================ */
  function bindHeaderTriggers() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-sidebar-button][data-sidebar-id="' + DRAWER_ID + '"], [data-np-cart-open]');
      if (!btn) return;
      var drawer = document.getElementById(DRAWER_ID) || document.querySelector(DRAWER_TAG);
      if (!drawer || typeof drawer.toggle !== 'function') return;
      // Capture phase + stopImmediatePropagation so the theme's own <sidebar-button>
      // click handler (target phase) never also fires — avoids a double-toggle where
      // both it and us call #CartDrawer.toggle().
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      drawer.toggle();
    }, true);
  }

  /* ============================================================
     Public helper: open the drawer (mirrors NPCartSync.openDrawer for callers
     that want to target THIS drawer explicitly during development).
     ============================================================ */
  window.NPCartDrawer = {
    open: function () {
      var d = document.getElementById(DRAWER_ID) || document.querySelector(DRAWER_TAG);
      return d && d.open ? d.open() : Promise.resolve();
    },
    close: function () {
      var d = document.getElementById(DRAWER_ID) || document.querySelector(DRAWER_TAG);
      return d && d.close ? d.close() : Promise.resolve();
    },
    el: function () { return document.getElementById(DRAWER_ID) || document.querySelector(DRAWER_TAG); }
  };

  /* ---------- boot ---------- */
  function boot() {
    if (!window.customElements) return;
    defineQuantityBtn();
    defineQuantityComponent();
    defineRemoveButton();
    defineCartItem();
    defineCartDrawer();
    bindHeaderTriggers();
  }

  ready(boot);
  document.addEventListener('shopify:section:load', function (e) {
    // Re-mount if our section reloaded in the theme editor.
    var drawer = e.target && e.target.querySelector ? e.target.querySelector(DRAWER_TAG) : null;
    if (drawer && typeof drawer.mount === 'function') { drawer.__wired = false; drawer.connectedCallback(); }
  });
})();
